/*
 * Behaviour for the B2B quote form.
 *
 * 1. Repeatable product + quantity rows. The rows carry no `name` attributes, so
 *    nothing from them submits directly; on submit they are serialised into a
 *    single hidden `contact[Order lines]` field, which keeps the notification
 *    email readable however many lines the buyer adds.
 *
 * 2. Phone number split into a calling-code dropdown and a number box, matching
 *    the contact page. India and the US are validated strictly; every other
 *    country falls back to a general length check so the form still accepts
 *    buyers anywhere in the world.
 */
(function () {
  var STRICT = {
    '+91': {
      pattern: /^[6-9]\d{9}$/,
      message: 'Enter a valid 10-digit Indian mobile number starting with 6, 7, 8 or 9.'
    },
    '+1': {
      pattern: /^[2-9]\d{2}[2-9]\d{6}$/,
      message: 'Enter a valid 10-digit US or Canadian number.'
    }
  };

  var GENERIC = {
    pattern: /^\d{6,14}$/,
    message: 'Enter a valid phone number, digits only, without the country code.'
  };

  function digitsOnly(value) {
    return (value || '').replace(/\D/g, '');
  }

  function setupPhone(root, form) {
    var code = root.querySelector('[data-phone-code]');
    var number = root.querySelector('[data-phone-number]');
    if (!code || !number) return null;

    function rule() {
      return STRICT[code.value] || GENERIC;
    }

    function validate() {
      var value = number.value.trim();
      if (value === '') {
        // Let the native `required` message handle the empty case.
        number.setCustomValidity('');
        return;
      }
      var active = rule();
      number.setCustomValidity(active.pattern.test(digitsOnly(value)) ? '' : active.message);
    }

    // If the server re-rendered the form with a combined value, split it back out.
    (function restore() {
      var raw = (number.value || '').trim();
      if (raw.charAt(0) !== '+') return;
      var options = Array.prototype.map.call(code.options, function (o) {
        return o.value;
      });
      options
        .sort(function (a, b) {
          return b.length - a.length;
        })
        .some(function (dial) {
          if (dial && raw.indexOf(dial) === 0) {
            code.value = dial;
            number.value = raw.slice(dial.length).trim();
            return true;
          }
          return false;
        });
    })();

    number.addEventListener('input', validate);
    number.addEventListener('blur', validate);
    code.addEventListener('change', function () {
      validate();
      if (number.value.trim() !== '' && typeof number.reportValidity === 'function') {
        number.reportValidity();
      }
    });
    validate();

    return {
      validate: validate,
      combine: function () {
        var local = digitsOnly(number.value);
        if (local) number.value = code.value + ' ' + local;
      }
    };
  }

  function init(root) {
    if (!root || root.dataset.b2bQuoteInit === 'true') return;
    root.dataset.b2bQuoteInit = 'true';

    var form = root.querySelector('form');
    if (!form) return;

    var phone = setupPhone(root, form);

    var list = root.querySelector('[data-lines]');
    var template = root.querySelector('[data-line-template]');
    var addButton = root.querySelector('[data-add-line]');
    var serialised = root.querySelector('[data-lines-value]');
    var errorEl = root.querySelector('[data-lines-error]');
    var maxLines = parseInt(root.dataset.maxLines, 10) || 10;

    if (!list || !template || !serialised) return;

    function rows() {
      return Array.prototype.slice.call(list.querySelectorAll('[data-line]'));
    }

    function refresh() {
      var all = rows();
      all.forEach(function (row, index) {
        var label = row.querySelector('[data-line-number]');
        if (label) label.textContent = 'Product ' + (index + 1);
        var remove = row.querySelector('[data-remove-line]');
        if (remove) remove.hidden = all.length < 2;
      });
      if (addButton) addButton.hidden = all.length >= maxLines;
    }

    /*
      `shouldFocus` is only ever true when the shopper presses "Add another
      product". The first row is created during page setup, and focusing a field
      then would make the browser jump the viewport down to the product dropdown
      the moment the page opened.
    */
    function addRow(shouldFocus) {
      if (rows().length >= maxLines) return;
      list.appendChild(template.content.cloneNode(true));
      refresh();
      if (!shouldFocus) return;
      var added = rows().pop();
      var firstSelect = added && added.querySelector('select');
      if (firstSelect) firstSelect.focus();
    }

    list.addEventListener('click', function (event) {
      var button = event.target.closest('[data-remove-line]');
      if (!button) return;
      var row = button.closest('[data-line]');
      if (row && rows().length > 1) {
        row.remove();
        refresh();
      }
    });

    if (addButton) {
      addButton.addEventListener('click', function (event) {
        event.preventDefault();
        addRow(true);
      });
    }

    // Page setup: build the first row without stealing focus or scroll position.
    if (!rows().length) addRow(false);
    refresh();

    form.addEventListener('submit', function (event) {
      if (phone) phone.validate();

      var lines = [];
      rows().forEach(function (row) {
        var product = row.querySelector('[data-line-product]');
        var quantity = row.querySelector('[data-line-quantity]');
        if (!product || !product.value) return;
        lines.push(product.value + ' - ' + (quantity && quantity.value ? quantity.value : 'quantity not specified'));
      });

      if (!lines.length) {
        event.preventDefault();
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = 'Please choose at least one product and quantity.';
        }
        var firstSelect = list.querySelector('select');
        if (firstSelect) firstSelect.focus();
        return;
      }

      if (errorEl) errorEl.hidden = true;

      // Stop here if anything else on the form is invalid, so the phone number
      // is not rewritten into its combined form on a failed submit.
      if (!form.checkValidity()) {
        event.preventDefault();
        if (typeof form.reportValidity === 'function') form.reportValidity();
        return;
      }

      serialised.value = lines
        .map(function (line, index) {
          return index + 1 + '. ' + line;
        })
        .join('\n');

      if (phone) phone.combine();
    });
  }

  function boot() {
    document.querySelectorAll('[data-b2b-quote-form]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
